import React, { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Building2, ArrowLeft } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { organizationsApi, type OrganizationInvitationDetails } from 'lib/api'

export function OrganizationSetup() {
  const [params] = useSearchParams()
  const token = useMemo(() => params.get('token') || '', [params])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [invitation, setInvitation] = useState<OrganizationInvitationDetails | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [setupCompleted, setSetupCompleted] = useState(false)
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [resendEmail, setResendEmail] = useState('')

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setError('Missing invitation token')
        setLoading(false)
        return
      }
      try {
        const response = await organizationsApi.validateInvitation(token)
        setInvitation(response.data)
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Invalid invitation')
      } finally {
        setLoading(false)
      }
    }
    validate()
  }, [token])

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords don't match")
      return
    }

    setSubmitting(true)
    try {
      const response = await organizationsApi.acceptInvitation({
        token,
        name: name.trim(),
        password,
        confirm_password: confirmPassword,
      })
      setMessage(response.data.message || 'Account setup complete. You can now login.')
      setSetupCompleted(true)
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to complete setup')
    } finally {
      setSubmitting(false)
    }
  }

  const handleResend = async () => {
    const email = invitation?.email || resendEmail.trim()
    if (!email) {
      setError('Email is required to resend the invitation')
      return
    }
    setResending(true)
    setError('')
    setMessage('')
    try {
      const response = await organizationsApi.resendInvitation(token, email)
      setMessage(response.data.message || 'Invitation resent successfully')
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to resend invitation')
    } finally {
      setResending(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2 text-foreground">
          <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
            <Building2 className="h-5 w-5" />
          </div>
          <span className="text-lg font-semibold tracking-tight">Selektia</span>
        </div>

        <div className="bg-background border rounded-2xl p-6 sm:p-8 shadow-sm">
          <h1 className="text-2xl font-bold text-center mb-2">Organization admin setup</h1>
          {invitation && (
            <p className="text-sm text-muted-foreground text-center mb-6">
              {invitation.organization_name}
            </p>
          )}

          {message && <div className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded p-3 mb-4">{message}</div>}
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3 mb-4">{error}</div>}

          {loading ? (
            <p className="text-sm text-muted-foreground text-center">Validating invitation...</p>
          ) : setupCompleted ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground text-center">
                Your admin account has been configured successfully.
              </p>
              <Link to="/login" className="block">
                <Button className="w-full">Go to login</Button>
              </Link>
            </div>
          ) : invitation ? (
            <form onSubmit={handleAccept} className="space-y-4">
              <Input value={invitation.email} disabled />
              <Input
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Setting up...' : 'Complete setup'}
              </Button>
            </form>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Invitation expired or invalid.</p>
              <Input
                type="email"
                placeholder="Admin email used for the invitation"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
              />
              <Button onClick={handleResend} disabled={resending || !token} className="w-full">
                {resending ? 'Resending...' : 'Resend invitation'}
              </Button>
            </div>
          )}

          {!setupCompleted && (
            <div className="mt-6 text-center">
              <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" />
                Back to login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrganizationSetup
